import { object, string } from "yup";
import { config } from "@/config";

/** Shared form values for recover-username flows (personal info / recover email) */
export interface RecoverUsernameFormValues {
  lastName: string;
  ssn: string;
}

/** Translated messages for recover-username field validation (recover email flow) */
export interface RecoverUsernameValidationMessages {
  validLastName: string;
  validSsn: string;
}

/** Shared validation schema for lastName and SSN; pass strings from i18n (e.g. errors.validLastName). */
export const createRecoverUsernameValidationSchema = (
  messages: RecoverUsernameValidationMessages,
) =>
  object().shape({
    lastName: string()
      .nullable()
      .ensure()
      .required(messages.validLastName)
      .min(2, messages.validLastName)
      // Allow letters, spaces, apostrophes, hyphens, and periods (for suffixes like III.)
      .matches(/^[A-Za-z .'-]+$/, messages.validLastName)
      .test("min-letters", messages.validLastName, (value) => {
        const letterCount = (value ?? "").replace(/[^A-Za-z]/g, "").length;
        return letterCount >= 2;
      }),
    ssn: string()
      .nullable()
      .ensure()
      .required(messages.validSsn)
      .test(
        "must-have-9-digits",
        messages.validSsn,
        (value: string) => {
          const digitsOnly = (value ?? "").replace(/\D/g, "");
          return digitsOnly.length === 9;
        },
      ),
  });

/** Set recover username headers in config for unauthHttpRequest */
export const setRecoverUsernameHeaders = (
  captchaToken: string | undefined,
  guid: string,
) => {
  if (captchaToken) {
    config.setEnv("recoverUsernameCaptchaToken", captchaToken);
  }
  config.setEnv("recoverUsernameRequestGuid", guid);
};

/** Clear recover username headers from config */
export const clearRecoverUsernameHeaders = () => {
  config.setEnv("recoverUsernameCaptchaToken", "");
  config.setEnv("recoverUsernameRequestGuid", "");
};

/** Error object for reCAPTCHA not initialized */
export const createRecaptchaNotInitializedError = () => ({
  data: {
    errorDetails: {
      message:
        "reCAPTCHA validation is not available. Please refresh the page and try again.",
      code: "RECAPTCHA_NOT_INITIALIZED",
      reason: "reCAPTCHA provider not initialized",
    },
  },
  response: {
    data: {
      errorDetails: {
        message:
          "reCAPTCHA validation is not available. Please refresh the page and try again.",
        code: "RECAPTCHA_NOT_INITIALIZED",
        reason: "reCAPTCHA provider not initialized",
      },
    },
  },
});

/** Error object for reCAPTCHA token generation failure */
export const createRecaptchaGenerationFailedError = (error: unknown) => ({
  data: {
    errorDetails: {
      message: "reCAPTCHA validation failed. Please try again.",
      code: "RECAPTCHA_GENERATION_FAILED",
      reason:
        error instanceof Error ? error.message : "reCAPTCHA token generation failed",
    },
  },
  response: {
    data: {
      errorDetails: {
        message: "reCAPTCHA validation failed. Please try again.",
        code: "RECAPTCHA_GENERATION_FAILED",
        reason:
          error instanceof Error ? error.message : "reCAPTCHA token generation failed",
      },
    },
  },
});
